import React, { useCallback, useEffect, useState } from "react"
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore"
import { CheckCircle, RefreshCw, Search, Trophy, X } from "lucide-react"
import { db } from "../firebase"
import { getTeamLogoWithFallback } from "../utils/teamLogoUtils"
import { useAuth } from "../context/AuthContext"
import Filters from "./Filters"
import Header from "./header"

const SPORT_KEYS_FOR_REFRESH = [
  "mma_mixed_martial_arts",
  "americanfootball_ncaaf",
  "americanfootball_nfl",
  "americanfootball_nfl_preseason",
  "baseball_mlb",
  "basketball_nba",
  "basketball_wnba",
  "basketball_ncaab",
  "basketball_wncaab",
  "boxing_boxing",
]
const SCORES_PAGE_SIZE = 20
const LIVE_WINDOW_MS = (3 * 60 + 15) * 60 * 1000 // 3h 15m
const MANUAL_REFRESH_USER_IDS = new Set([
  "uRYijpDdFMVv2f05wmT7bppRkzI2",
  "01w2FQlCEUPr6aTMy9d1Wn9KIzJ2",
])
const RAPID_API_KEYS = [
  { name: "Rivals", key: "7837c2a021msh419c1695e853662p1e524cjsne171fd94cdbc" },
  { name: "MaxoutPro", key: "5d7bb769famshed0e0109c9e833dp1baafajsna0791d51d148" },
  { name: "Paul Ter", key: "5ad3ce5db5msh0c9416b8dacf66ep11ab1cjsn8bd69263541a" },
  { name: "Aura", key: "3c9e529bddmshcf9f3ef31e53cddp1a40cfjsnbadee9b10f37" },
  { name: "Primetime", key: "da596a0e8fmsh0c5fd3985ac6bfcp186cb1jsn15f1405a7385" },
  { name: "Sportsfit", key: "c12c89ec1dmsh5b70dac4ddb7b26p1b5991jsn8088c4f188d0" },
  { name: "Trademetricx", key: "6d969a53dbmsh2b12d149b875a1ep1aae1ejsncb1005933fe0" },
]
const SCORES_REQUEST_DELAY_MS = 1500
const SCORES_RATE_LIMIT_WAIT_MS = 5000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

class ScoresFetchError extends Error {
  constructor(message, status) {
    super(message)
    this.status = status
    this.name = "ScoresFetchError"
  }
}

async function fetchScoresWithKey(sportKey, apiKey) {
  const response = await fetch(
    `https://odds.p.rapidapi.com/v4/sports/${sportKey}/scores?daysFrom=3`,
    {
      method: "GET",
      headers: {
        "X-RapidAPI-Key": apiKey,
        "X-RapidAPI-Host": "odds.p.rapidapi.com",
      },
    }
  )

  let data
  try {
    data = await response.json()
  } catch {
    throw new Error(`Invalid JSON response (${response.status})`)
  }

  if (!response.ok) {
    const detail = typeof data?.message === "string" ? data.message : response.statusText
    throw new ScoresFetchError(`HTTP ${response.status}: ${detail}`, response.status)
  }

  if (!Array.isArray(data)) {
    throw new Error("Unexpected response format (expected scores array)")
  }

  return data
}

async function saveScoresToFirestore(sportKey, fetchedScores) {
  const docRef = doc(collection(db, "Scores"), sportKey)
  const existingDoc = await getDoc(docRef)
  const existingScores = existingDoc.exists() ? existingDoc.data()?.scores || [] : []

  const scoresById = new Map()
  for (const item of existingScores) {
    if (item?.id) scoresById.set(item.id, item)
  }
  for (const item of fetchedScores) {
    if (!item?.id) continue
    scoresById.set(item.id, item)
  }

  const mergedScores = Array.from(scoresById.values()).sort(
    (a, b) => new Date(b.commence_time || 0) - new Date(a.commence_time || 0)
  )

  await setDoc(
    docRef,
    {
      scores: mergedScores,
      totalFetched: fetchedScores.length,
      totalSaved: mergedScores.length,
      lastFetchedAt: new Date().toISOString(),
    },
    { merge: true }
  )

  return mergedScores
}

async function refreshScoresForAllSports(sportKeys) {
  const excludedKeyIndices = new Set()
  let preferredKeyIndex = 0
  const results = []

  for (const sportKey of sportKeys) {
    let saved = false
    let lastError = null

    const keyOrder = []
    for (let offset = 0; offset < RAPID_API_KEYS.length; offset++) {
      const idx = (preferredKeyIndex + offset) % RAPID_API_KEYS.length
      if (!excludedKeyIndices.has(idx)) keyOrder.push(idx)
    }

    for (const keyIndex of keyOrder) {
      const { name, key } = RAPID_API_KEYS[keyIndex]
      try {
        const fetchedScores = await fetchScoresWithKey(sportKey, key)
        await saveScoresToFirestore(sportKey, fetchedScores)
        preferredKeyIndex = keyIndex
        results.push({ sportKey, success: true, apiKeyUsed: name, count: fetchedScores.length })
        saved = true
        console.info(`Scores for ${sportKey} refreshed using ${name} API key (${fetchedScores.length} games)`)
        await sleep(SCORES_REQUEST_DELAY_MS)
        break
      } catch (error) {
        lastError = error
        const status = error instanceof ScoresFetchError ? error.status : null
        console.warn(`Scores fetch failed for ${sportKey} with ${name} key:`, error.message)

        if (status === 403) {
          excludedKeyIndices.add(keyIndex)
          continue
        }
        if (status === 429) {
          await sleep(SCORES_RATE_LIMIT_WAIT_MS)
          continue
        }
      }
    }

    if (!saved) {
      results.push({
        sportKey,
        success: false,
        error: lastError?.message || "All API keys failed",
      })
    }
  }

  return results
}

function Scores() {
  const { currentUser } = useAuth()
  const canManualRefreshScores = MANUAL_REFRESH_USER_IDS.has(currentUser?.uid)
  const [currentView, setCurrentView] = useState("settings")
  const [scores, setScores] = useState([])
  const [visibleCount, setVisibleCount] = useState(SCORES_PAGE_SIZE)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedSport, setSelectedSport] = useState("all")
  const [selectedSportTitle, setSelectedSportTitle] = useState("All")
  const [searchQuery, setSearchQuery] = useState("")

  const loadScores = useCallback(async () => {
    setLoading(true)
    try {
      const snapshot = await getDocs(collection(db, "Scores"))
      const allGames = snapshot.docs.flatMap((d) => {
        const data = d.data()
        return Array.isArray(data?.scores) ? data.scores : []
      })

      const nowMs = Date.now()
      const completedOrLive = allGames.filter((game) => {
        if (!game || !Array.isArray(game?.scores) || game.scores.length < 2) return false
        if (game?.completed === true) return true

        const startMs = new Date(game.commence_time || 0).getTime()
        if (Number.isNaN(startMs)) return false
        return nowMs >= startMs && nowMs <= startMs + LIVE_WINDOW_MS
      })

      const bySport =
        selectedSport === "all"
          ? completedOrLive
          : completedOrLive.filter((game) => game.sport_key === selectedSport)

      const normalizedSearch = (searchQuery || "").trim().toLowerCase()
      const bySportAndSearch = normalizedSearch
        ? bySport.filter((game) => {
            const home = (game?.home_team || "").toLowerCase()
            const away = (game?.away_team || "").toLowerCase()
            return home.includes(normalizedSearch) || away.includes(normalizedSearch)
          })
        : bySport

      bySportAndSearch.sort(
        (a, b) =>
          new Date(b.commence_time || b.last_update || 0) -
          new Date(a.commence_time || a.last_update || 0)
      )

      setScores(bySportAndSearch)
    } catch (error) {
      console.error("Error loading completed scores:", error)
      setScores([])
    } finally {
      setLoading(false)
    }
  }, [searchQuery, selectedSport])

  useEffect(() => {
    loadScores()
  }, [loadScores])

  const handleFilterSelect = (sportsKey, sportsTitle) => {
    setSelectedSport(sportsKey)
    setSelectedSportTitle(sportsTitle)
    setVisibleCount(SCORES_PAGE_SIZE)
  }

  const handleRefreshScores = async () => {
    setRefreshing(true)
    try {
      const results = await refreshScoresForAllSports(SPORT_KEYS_FOR_REFRESH)
      const failed = results.filter((result) => !result.success)
      if (failed.length > 0) {
        console.error(
          `Failed to refresh ${failed.length}/${SPORT_KEYS_FOR_REFRESH.length} sports:`,
          failed.map((result) => `${result.sportKey}: ${result.error}`)
        )
      }
      await loadScores()
    } catch (error) {
      console.error("Error refreshing scores:", error)
    } finally {
      setRefreshing(false)
    }
  }

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + SCORES_PAGE_SIZE)
  }

  const visibleScores = scores.slice(0, visibleCount)
  const hasMoreScores = scores.length > visibleCount

  useEffect(() => {
    setVisibleCount(SCORES_PAGE_SIZE)
  }, [selectedSport, searchQuery])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const getWinnerName = (game) => {
    if (!Array.isArray(game.scores) || game.scores.length < 2) return null
    const [homeScoreObj, awayScoreObj] = game.scores
    const homeScore = Number.parseInt(homeScoreObj?.score, 10)
    const awayScore = Number.parseInt(awayScoreObj?.score, 10)
    if (Number.isNaN(homeScore) || Number.isNaN(awayScore)) return null
    if (homeScore > awayScore) return homeScoreObj?.name
    if (awayScore > homeScore) return awayScoreObj?.name
    return "Tie"
  }

  const getTeamScore = (game, teamName) => {
    if (!Array.isArray(game?.scores)) return null
    const matched = game.scores.find((item) => item?.name === teamName)
    return matched || null
  }

  const isGameLive = (game) => {
    if (!game || game.completed === true) return false
    const startMs = new Date(game.commence_time || 0).getTime()
    if (Number.isNaN(startMs)) return false
    const nowMs = Date.now()
    return nowMs >= startMs && nowMs <= startMs + LIVE_WINDOW_MS
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-black text-white">
      <Header onToggle={setCurrentView} currentView={currentView} onShowNotifications={() => {}} />

      <div className="flex flex-grow">
        <div className="flex-grow py-4 px-2 md:px-4 overflow-auto">
          <div className="w-full rounded-xl mb-6 min-h-full bg-gray-900/30 backdrop-blur-sm border border-gray-800 overflow-hidden">
            <div className="w-full h-[150px] md:h-[220px] bg-cover bg-center relative">
              <img
                alt=""
                src={require("../assets/mainBackground.png") || "/placeholder.svg"}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900"></div>
            </div>

            <Filters onFilterSelect={handleFilterSelect} />

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center px-4 md:px-5 py-5">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-white text-lg font-bold">{selectedSportTitle || "All"} Scores</h1>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-gray-400 text-sm">Completed + Live Games</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center px-4 md:px-5 pb-2">
              <div className="relative max-w-md w-full sm:flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by team name"
                  className="w-full pl-10 pr-10 h-10 rounded-lg bg-gray-800/70 border border-gray-700 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/70 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </div>

              {canManualRefreshScores ? (
                <button
                  onClick={handleRefreshScores}
                  disabled={refreshing}
                  className="flex items-center justify-center gap-2 px-4 py-2 h-10 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 text-white rounded-lg transition-colors w-full sm:w-auto shrink-0"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                  {refreshing ? "Refreshing..." : "Refresh Scores"}
                </button>
              ) : null}
            </div>

            <div className="px-4 pb-4">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="bg-gray-800/70 p-4 rounded-xl">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
                    <span className="text-white block mt-4">Loading scores...</span>
                  </div>
                </div>
              ) : scores.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="bg-gray-800/70 p-6 rounded-xl inline-block mb-4">
                    <Trophy className="w-12 h-12 text-gray-500 mx-auto" />
                  </div>
                  <h2 className="text-xl text-white font-medium mb-2">No Live or Completed Scores</h2>
                  <p className="text-gray-400">No live/completed games found for the selected sport.</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 text-center">
                    <p className="text-gray-400 text-sm">
                      Showing {visibleScores.length} of {scores.length} {selectedSportTitle === "All" ? "games" : `${selectedSportTitle} games`}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    {visibleScores.map((game) => {
                      const live = isGameLive(game)
                      const winner = getWinnerName(game)
                      const home = getTeamScore(game, game.home_team) || game.scores?.[0]
                      const away = getTeamScore(game, game.away_team) || game.scores?.[1]
                      const homeLogo = getTeamLogoWithFallback(game.home_team, game.sport_key)
                      const awayLogo = getTeamLogoWithFallback(game.away_team, game.sport_key)
                      return (
                        <div
                          key={game.id}
                          className={`backdrop-blur-sm border rounded-xl p-4 transition-colors ${
                            live
                              ? "bg-emerald-900/20 border-emerald-500/40 hover:bg-emerald-900/30"
                              : "bg-gray-800/70 border-gray-700 hover:bg-gray-800/80"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              {live ? (
                                <>
                                  <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                  </span>
                                  <span className="font-semibold text-sm text-red-400 tracking-wide">LIVE</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                                  <span className="font-medium text-sm text-emerald-400">Final</span>
                                </>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">{formatDate(game.commence_time)}</div>
                              <div className="text-xs text-gray-500">{formatTime(game.commence_time)}</div>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3 items-center mb-3">
                            <div className="text-right min-w-0">
                              <div className="flex items-center justify-end gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-white truncate">{game.away_team}</div>
                                </div>
                                <img
                                  src={awayLogo}
                                  alt={game.away_team}
                                  className="w-7 h-7 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null
                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(game.away_team)}&background=1f2937&color=ffffff&size=96`
                                  }}
                                />
                              </div>
                              <div className={`text-lg font-bold mt-1 ${winner === away?.name ? "text-emerald-400" : "text-gray-300"}`}>
                                {away?.score ?? "-"}
                              </div>
                            </div>

                            <div className="text-center">
                              <div className="text-gray-500 text-xs font-medium">VS</div>
                              <div className="text-xs mt-1">
                                {winner && winner !== "Tie" ? (
                                  <span className="text-emerald-400">Winner</span>
                                ) : (
                                  <span className="text-yellow-400">Tie</span>
                                )}
                              </div>
                            </div>

                            <div className="text-left min-w-0">
                              <div className="flex items-center gap-2">
                                <img
                                  src={homeLogo}
                                  alt={game.home_team}
                                  className="w-7 h-7 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null
                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(game.home_team)}&background=1f2937&color=ffffff&size=96`
                                  }}
                                />
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-white truncate">{game.home_team}</div>
                                </div>
                              </div>
                              <div className={`text-lg font-bold mt-1 ${winner === home?.name ? "text-emerald-400" : "text-gray-300"}`}>
                                {home?.score ?? "-"}
                              </div>
                            </div>
                          </div>

                          <div className="pt-3 border-t border-gray-700">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs text-gray-400 truncate">{game.sport_title}</span>
                              {game.last_update ? (
                                <span className="text-xs text-gray-500">Updated: {formatTime(game.last_update)}</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {hasMoreScores ? (
                    <div className="flex justify-center pt-5">
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        className="rounded-full border border-gray-700 bg-gray-800/50 px-4 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-700/60"
                      >
                        Load more 20
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Scores
